import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../../contexts/PusherContext';
import { useAgent } from '../../../contexts/AgentContext';
import {
  AgentEvents,
  AgentStreamEvents,
  ProjectPlanUpdatedEvent,
  SubThreadUpdateEvent,
  SubThreadStatus,
  DesiredActionKey,
  ResponseArtifactDTO,
  UpdateResponseArtifactRequest,
  UpdateResponseArtifactResponse,
  GoogleLinkedAccount,
  GetGoogleLinkedAccountsResponse,
} from '@dreamai/types';
import api from '../../../api';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getActionKeyLabel } from '../../../lib/desiredActionFields';
import ResponseArtifactModal from '../sidebar/ResponseArtifactModal';
import ResearchBlockModal from './ResearchBlockModal';
import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';
import { ReactComponent as ActionSendIcon } from '../../../icons/action-send.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionCalendarIcon } from '../../../icons/action-calendar.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as TagIcon } from '../../../icons/tag.svg';
import { ReactComponent as SwitchArrowsIcon } from '../../../icons/switch-arrows.svg';
import { ReactComponent as InfoCircleIcon } from '../../../icons/info-circle.svg';
import { ReactComponent as AgentSearchIcon } from '../../../icons/agent-search.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import { ReactComponent as CheckmarkIcon } from '../../../icons/checkmark.svg';

interface ProjectPlanChecklistProps {
  threadId: string | null;
  threadName?: string;
}

interface PlanClarifyingQuestion {
  fieldKey: string;
  label: string;
  type: 'text' | 'select' | 'number';
  options?: string[];
  required: boolean;
  defaultValue?: string;
  currency?: string;
  mustBeWithinRadius?: boolean;
  answer?: string;
}

interface PlanStep {
  description: string;
  desiredAction: string;
  checked: boolean;
  dueDate?: string;
  clarifyingQuestions?: PlanClarifyingQuestion[];
  children?: PlanStep[];
  subThreadId?: string;
  artifactId?: string;
}

const PLAN_ACTION_ICONS: Record<
  string,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionBuyIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: TagIcon,
  act: SwitchArrowsIcon,
  buy_and_act: TagIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  private_action: AgentSearchIcon,
  create: ActionCreateIcon,
  other: ActionOtherIcon,
};

const PLAN_ACTION_LABELS: Record<string, string> = {
  private_action: 'Agent Action',
};

function getPlanActionLabel(key: string, agentName?: string): string {
  if (PLAN_ACTION_LABELS[key]) return PLAN_ACTION_LABELS[key];
  return getActionKeyLabel(key as DesiredActionKey, agentName);
}

function formatNumber(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

function getCurrencySymbol(code?: string): string {
  if (!code) return '';
  return getSymbolFromCurrency(code) || code;
}

const FROM_EMAIL_FIELD_KEY = 'from_email_address';

/** Convert snake_case fieldKey to a short Title Case label */
function shortLabel(fieldKey: string): string {
  return fieldKey
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Format an answered pill: "Max Spend: £500" or "Hotel Style: Luxury" */
function formatPillAnswer(q: PlanClarifyingQuestion, value: string): string {
  const label = shortLabel(q.fieldKey);
  if (q.type === 'number' && q.currency) {
    const sym = getCurrencySymbol(q.currency);
    return `${label}: ${sym}${formatNumber(value)}`;
  }
  if (q.type === 'number') {
    return `${label}: ${formatNumber(value)}`;
  }
  return `${label}: ${value}`;
}

interface ActiveQuestion {
  stepIdx: number;
  childIdx?: number;
  fieldKey: string;
}

const ProjectPlanChecklist: React.FC<ProjectPlanChecklistProps> = ({
  threadId,
  threadName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [steps, setSteps] = useState<PlanStep[]>([]);
  const [loading, setLoading] = useState(true);

  // Sub-thread status tracking: subThreadId -> status
  const [subThreadStatuses, setSubThreadStatuses] = useState<
    Record<string, SubThreadStatus>
  >({});

  // Accordion state for section headers (collapsed by default: false = expanded)
  const [collapsedSections, setCollapsedSections] = useState<
    Record<number, boolean>
  >({});
  // Track whether we've applied initial auto-collapse for these steps
  const autoCollapsedRef = useRef<string | null>(null);

  // Artifact modal state
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [activeArtifact, setActiveArtifact] =
    useState<ResponseArtifactDTO | null>(null);

  // Pill interaction state
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestion | null>(
    null,
  );
  const [activeQuestionInput, setActiveQuestionInput] = useState('');
  const [savingField, setSavingField] = useState(false);

  // Radius checking state — mirrors the todo system
  const [radiusChecking, setRadiusChecking] = useState<Record<string, boolean>>(
    {},
  );
  const [radiusResults, setRadiusResults] = useState<Record<string, boolean>>(
    {},
  );

  // Connected email accounts for from_email_address selects
  const [connectedEmails, setConnectedEmails] = useState<string[]>([]);

  const compositeKey = (
    stepIdx: number,
    fieldKey: string,
    childIdx?: number,
  ) =>
    childIdx !== undefined
      ? `${stepIdx}:c${childIdx}:${fieldKey}`
      : `${stepIdx}:${fieldKey}`;

  // Fetch connected email accounts
  useEffect(() => {
    let cancelled = false;
    const fetchEmails = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetGoogleLinkedAccountsResponse>(
          '/google-link',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) {
          setConnectedEmails(
            res.data.linkedAccounts.map((a: GoogleLinkedAccount) => a.email),
          );
        }
      } catch {
        // Not critical — selects will just be empty
      }
    };
    fetchEmails();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  // Check if an address is within the Dreamster operating radius
  const checkRadiusForField = useCallback(
    async (key: string, value: string) => {
      if (!value.trim()) return;
      setRadiusChecking((prev) => ({ ...prev, [key]: true }));
      try {
        const token = await getAccessTokenSilently();
        const response = await api.post(
          '/user-todos/check-radius',
          { address: value },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const withinRadius = response.data?.withinRadius ?? true;
        setRadiusResults((prev) => ({ ...prev, [key]: withinRadius }));
      } catch {
        setRadiusResults((prev) => ({ ...prev, [key]: true }));
      } finally {
        setRadiusChecking((prev) => ({ ...prev, [key]: false }));
      }
    },
    [getAccessTokenSilently],
  );

  // Trigger radius checks when plan loads (for existing answers on radius fields)
  useEffect(() => {
    const checkStep = (step: PlanStep, stepIdx: number, childIdx?: number) => {
      if (step.checked || !step.clarifyingQuestions) return;
      for (const q of step.clarifyingQuestions) {
        if (q.mustBeWithinRadius && q.answer) {
          const ck = compositeKey(stepIdx, q.fieldKey, childIdx);
          if (radiusResults[ck] === undefined && !radiusChecking[ck]) {
            checkRadiusForField(ck, q.answer);
          }
        }
      }
    };
    for (let i = 0; i < steps.length; i++) {
      checkStep(steps[i], i);
      if (steps[i].children) {
        for (let c = 0; c < steps[i].children!.length; c++) {
          checkStep(steps[i].children![c], i, c);
        }
      }
    }
  }, [steps]); // intentionally not including radiusResults/radiusChecking to avoid loops

  const fetchPlan = useCallback(async () => {
    if (!threadId) {
      setLoading(false);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/project-plans/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.success && response.data.plan?.steps) {
        setSteps(response.data.plan.steps);
      }
    } catch {
      // 404 is expected when no plan exists yet
    } finally {
      setLoading(false);
    }
  }, [threadId, getAccessTokenSilently]);

  // Save a single answer and refresh the plan
  const saveAnswer = useCallback(
    async (
      stepIdx: number,
      fieldKey: string,
      value: string,
      childIdx?: number,
      mustBeWithinRadius?: boolean,
    ) => {
      if (!threadId || !value.trim()) return;
      setSavingField(true);
      try {
        const token = await getAccessTokenSilently();
        await api.patch(
          `/project-plans/${threadId}/answers`,
          {
            stepIndex: stepIdx,
            fieldKey,
            answer: value,
            ...(childIdx !== undefined && { childIndex: childIdx }),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // Trigger radius check if needed
        if (mustBeWithinRadius) {
          checkRadiusForField(compositeKey(stepIdx, fieldKey, childIdx), value);
        }
        await fetchPlan();
      } catch (err) {
        console.error('[ProjectPlan] Failed to save answer:', err);
      } finally {
        setSavingField(false);
        setActiveQuestion(null);
        setActiveQuestionInput('');
      }
    },
    [threadId, getAccessTokenSilently, fetchPlan, checkRadiusForField],
  );

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Listen for real-time plan updates via Pusher
  useEffect(() => {
    if (!pusherChannel || !threadId) return;
    const handler = (event: ProjectPlanUpdatedEvent) => {
      if (event.threadId !== threadId) return;
      setSteps(event.steps as PlanStep[]);
    };
    pusherChannel.bind(AgentEvents.PROJECT_PLAN_UPDATED, handler);
    return () => {
      pusherChannel.unbind(AgentEvents.PROJECT_PLAN_UPDATED, handler);
    };
  }, [pusherChannel, threadId]);

  // Listen for sub-thread status updates so linked steps show live progress
  useEffect(() => {
    if (!pusherChannel || !threadId) return;
    const handler = (event: SubThreadUpdateEvent) => {
      if (event.parentThreadId !== threadId) return;
      const st = event.subThread;
      setSubThreadStatuses((prev) => ({ ...prev, [st.id]: st.status }));
    };
    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_UPDATE, handler);
    return () => {
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE, handler);
    };
  }, [pusherChannel, threadId]);

  const toggleSection = useCallback((idx: number) => {
    setCollapsedSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const openArtifactModal = useCallback(
    async (artifactId: string) => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<{
          success: boolean;
          artifact: ResponseArtifactDTO;
        }>(`/response-artifacts/artifact/${artifactId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setActiveArtifact(response.data.artifact);
          setArtifactModalOpen(true);
        }
      } catch (err) {
        console.error('[ProjectPlan] Failed to fetch artifact:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleSubmitArtifactResponse = useCallback(
    async (
      art: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: {
        cardType: 'agent' | 'user_card' | 'merchant_stored';
        credentialId?: string;
        cardHint?: string;
      },
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: art.id,
          status: 'actioned',
          userResponse: response,
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${art.id}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setActiveArtifact((prev) =>
          prev ? { ...prev, status: 'actioned', userResponse: response } : prev,
        );
        setArtifactModalOpen(false);
      } catch (error) {
        console.error(
          '[ProjectPlan] Error submitting artifact response:',
          error,
        );
      }
    },
    [getAccessTokenSilently],
  );

  // Get agent nickname for "Email from {name}" labels
  const { selectedAgent, agents } = useAgent();
  const agentName =
    selectedAgent?.label || agents[0]?.nickname || agents[0]?.name;

  // Auto-collapse sections where all items are completed.
  // Must be above the early return to maintain hooks order.
  const stepsFingerprint = steps.map((s) => s.checked).join(',');
  useEffect(() => {
    if (!steps.length || autoCollapsedRef.current === stepsFingerprint) return;
    autoCollapsedRef.current = stepsFingerprint;
    const autoCollapsed: Record<number, boolean> = {};
    steps.forEach((step, idx) => {
      if (step.children?.length) {
        if (step.children.every((c) => c.checked)) {
          autoCollapsed[idx] = true;
        }
      }
    });
    // Also handle default groups (orphan steps)
    type TmpGroup = { firstIdx: number; items: PlanStep[] };
    const tmpGroups: TmpGroup[] = [];
    steps.forEach((step, idx) => {
      if (step.children?.length) return;
      const last = tmpGroups[tmpGroups.length - 1];
      if (
        last &&
        !steps.slice(last.firstIdx + 1, idx).some((s) => s.children?.length)
      ) {
        last.items.push(step);
      } else {
        tmpGroups.push({ firstIdx: idx, items: [step] });
      }
    });
    tmpGroups.forEach((g) => {
      if (g.items.length > 0 && g.items.every((s) => s.checked)) {
        autoCollapsed[-(g.firstIdx + 1)] = true;
      }
    });
    setCollapsedSections((prev) => ({ ...prev, ...autoCollapsed }));
  }, [stepsFingerprint]);

  if (loading || steps.length === 0) return null;

  const confirmPill = (
    q: PlanClarifyingQuestion,
    stepIdx: number,
    val: string,
    childIdx?: number,
  ) => {
    if (!val.trim()) return;
    saveAnswer(stepIdx, q.fieldKey, val, childIdx, q.mustBeWithinRadius);
  };

  const renderQuestionPills = (
    step: PlanStep,
    stepIdx: number,
    childIdx?: number,
  ) => {
    if (step.checked || !step.clarifyingQuestions?.length) return null;
    const aq =
      activeQuestion &&
      activeQuestion.stepIdx === stepIdx &&
      activeQuestion.childIdx === childIdx
        ? activeQuestion
        : null;
    const activeQ = aq
      ? step.clarifyingQuestions.find((q) => q.fieldKey === aq.fieldKey)
      : null;
    const activeOpts = activeQ
      ? activeQ.fieldKey === FROM_EMAIL_FIELD_KEY && activeQ.type === 'select'
        ? connectedEmails
        : activeQ.options || []
      : [];
    const isSelect = activeQ?.type === 'select' && activeOpts.length > 0;

    return (
      <div className='plan-question-pills-row'>
        {aq === null ? (
          step.clarifyingQuestions.map((q) => {
            const ck = compositeKey(stepIdx, q.fieldKey, childIdx);
            const answered = q.answer;
            const isChk = radiusChecking[ck];
            const isOut =
              q.mustBeWithinRadius && answered && radiusResults[ck] === false;
            return (
              <button
                key={q.fieldKey}
                className={`question-pill${answered ? ' answered' : ''}${isOut ? ' outside-radius' : ''}${isChk ? ' checking' : ''}`}
                onClick={() => {
                  setActiveQuestion({
                    stepIdx,
                    childIdx,
                    fieldKey: q.fieldKey,
                  });
                  setActiveQuestionInput(answered || '');
                }}
              >
                {isChk
                  ? 'Checking area...'
                  : isOut
                    ? `${formatPillAnswer(q, answered!)} ⚠`
                    : answered
                      ? formatPillAnswer(q, answered)
                      : shortLabel(q.fieldKey) + '?'}
              </button>
            );
          })
        ) : isSelect ? (
          <>
            <button
              className='pill-back-btn'
              onClick={() => setActiveQuestion(null)}
            >
              <ChevronLeftIcon />
            </button>
            {activeOpts.map((opt) => (
              <button
                key={opt}
                className='question-pill option'
                onClick={() => confirmPill(activeQ!, stepIdx, opt, childIdx)}
              >
                {opt}
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              className='pill-back-btn'
              onClick={() => setActiveQuestion(null)}
            >
              <ChevronLeftIcon />
            </button>
            <div className='pill-input-wrapper'>
              <input
                type={activeQ?.type === 'number' ? 'number' : 'text'}
                className='pill-input'
                placeholder={activeQ?.label}
                value={activeQuestionInput}
                onChange={(e) => setActiveQuestionInput(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    activeQuestionInput.trim() &&
                    activeQ
                  )
                    confirmPill(
                      activeQ,
                      stepIdx,
                      activeQuestionInput.trim(),
                      childIdx,
                    );
                  if (e.key === 'Escape') {
                    setActiveQuestion(null);
                    setActiveQuestionInput('');
                  }
                }}
              />
            </div>
            <button
              className='pill-confirm-btn'
              disabled={!activeQuestionInput.trim() || savingField}
              onClick={() =>
                activeQ &&
                confirmPill(
                  activeQ,
                  stepIdx,
                  activeQuestionInput.trim(),
                  childIdx,
                )
              }
            >
              <CheckmarkIcon />
            </button>
          </>
        )}
      </div>
    );
  };

  /** Determine the check circle state for a step */
  const getCheckState = (
    step: PlanStep,
  ): 'complete' | 'artifact' | 'processing' | 'empty' => {
    if (step.checked) return 'complete';
    if (step.artifactId) return 'artifact';
    if (
      step.subThreadId &&
      subThreadStatuses[step.subThreadId] === 'processing'
    )
      return 'processing';
    return 'empty';
  };

  const renderCheckCircle = (step: PlanStep) => {
    const state = getCheckState(step);
    switch (state) {
      case 'complete':
        return (
          <div className='plan-check complete'>
            <svg width='10' height='8' viewBox='0 0 10 8' fill='none'>
              <path
                d='M1 4L3.5 6.5L9 1'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        );
      case 'artifact':
        return (
          <button
            className='plan-check artifact'
            onClick={() =>
              step.artifactId && openArtifactModal(step.artifactId)
            }
          >
            <span className='artifact-dot' />
          </button>
        );
      case 'processing':
        return (
          <div className='plan-check processing'>
            <TodoSpinnerIcon className='plan-check-spinner' />
          </div>
        );
      default:
        return <div className='plan-check empty' />;
    }
  };

  const renderStepRow = (
    step: PlanStep,
    stepIdx: number,
    childIdx?: number,
  ) => (
    <>
      {renderCheckCircle(step)}
      <div className='plan-item-content'>
        <span className='plan-item-text'>{step.description}</span>
        <div className='plan-item-tags'>
          {step.dueDate && (
            <span className='plan-date-pill'>{step.dueDate}</span>
          )}
          {!['private_action', 'other'].includes(
            step.desiredAction || 'private_action',
          ) && (
            <span className='plan-action-pill'>
              {React.createElement(
                PLAN_ACTION_ICONS[step.desiredAction] || ActionOtherIcon,
              )}
              {getPlanActionLabel(step.desiredAction, agentName)}
            </span>
          )}
        </div>
        {renderQuestionPills(step, stepIdx, childIdx)}
      </div>
    </>
  );

  const isResearchArtifact = activeArtifact
    ? !['multiple_choice', 'yes_or_no', 'form_input'].includes(
        activeArtifact.artifactType,
      )
    : false;

  const renderSectionCaret = () => (
    <svg
      className='plan-section-caret'
      xmlns='http://www.w3.org/2000/svg'
      width='8'
      height='6'
      viewBox='0 0 8 6'
      fill='none'
    >
      <path d='M4 6L0 -3.49691e-07L8 3.02842e-07L4 6Z' fill='currentColor' />
    </svg>
  );

  // Group steps into sections: real sections keep their children,
  // consecutive orphan steps (no children) are collected under a default heading.
  type SectionGroup =
    | { type: 'section'; step: PlanStep; idx: number }
    | { type: 'default'; steps: Array<{ step: PlanStep; idx: number }> };

  const groups: SectionGroup[] = [];
  steps.forEach((step, idx) => {
    if (step.children?.length) {
      groups.push({ type: 'section', step, idx });
    } else {
      const last = groups[groups.length - 1];
      if (last && last.type === 'default') {
        last.steps.push({ step, idx });
      } else {
        groups.push({ type: 'default', steps: [{ step, idx }] });
      }
    }
  });

  // Helper: get the items for a group
  const getGroupItems = (group: SectionGroup): PlanStep[] =>
    group.type === 'section'
      ? group.step.children || []
      : group.steps.map((s) => s.step);

  // Helper: get the collapse key for a group
  const getCollapseKey = (group: SectionGroup): number =>
    group.type === 'section' ? group.idx : -(group.steps[0].idx + 1);

  return (
    <div className='project-plan-checklist'>
      <ul className='plan-items'>
        {groups.map((group, gIdx) => {
          const items = getGroupItems(group);
          const completed = items.filter((s) => s.checked).length;
          const total = items.length;

          if (group.type === 'section') {
            const { step, idx } = group;
            const isCollapsed = !!collapsedSections[idx];
            return (
              <li key={`s-${idx}`} className='plan-section'>
                <button
                  className={`plan-section-header${isCollapsed ? ' collapsed' : ''}`}
                  onClick={() => toggleSection(idx)}
                >
                  {renderSectionCaret()}
                  <span className='plan-section-title'>{step.description}</span>
                  <span className='plan-section-count'>
                    {completed}/{total}
                  </span>
                </button>
                {!isCollapsed && (
                  <ul className='plan-children'>
                    {step.children!.map((child, cIdx) => (
                      <li
                        key={cIdx}
                        className={`plan-item plan-child-item${child.checked ? ' done' : ''}`}
                      >
                        {renderStepRow(child, idx, cIdx)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          }

          // Default group for orphan steps
          const collapseKey = -(group.steps[0].idx + 1);
          const isCollapsed = !!collapsedSections[collapseKey];
          return (
            <li key={`d-${gIdx}`} className='plan-section'>
              <button
                className={`plan-section-header${isCollapsed ? ' collapsed' : ''}`}
                onClick={() => toggleSection(collapseKey)}
              >
                {renderSectionCaret()}
                <span className='plan-section-title'>Plan Steps</span>
                <span className='plan-section-count'>
                  {completed}/{total}
                </span>
              </button>
              {!isCollapsed && (
                <ul className='plan-children'>
                  {group.steps.map(({ step, idx }) => (
                    <li
                      key={idx}
                      className={`plan-item plan-child-item${step.checked ? ' done' : ''}`}
                    >
                      {renderStepRow(step, idx)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {activeArtifact &&
        (isResearchArtifact && activeArtifact.projectAppId ? (
          <ResearchBlockModal
            show={artifactModalOpen}
            onClose={() => setArtifactModalOpen(false)}
            appId={activeArtifact.projectAppId}
            title={activeArtifact.title}
            artifactId={activeArtifact.id}
            desiredActions={activeArtifact.desiredActions}
            parentThreadId={activeArtifact.parentThreadId}
            backLabel={threadName}
          />
        ) : (
          <ResponseArtifactModal
            show={artifactModalOpen}
            onClose={() => setArtifactModalOpen(false)}
            artifacts={[activeArtifact]}
            initialIndex={0}
            onSubmitResponse={handleSubmitArtifactResponse}
            readOnly={activeArtifact.status === 'actioned'}
          />
        ))}
    </div>
  );
};

export default ProjectPlanChecklist;
